import { serialize } from "object-to-formdata";
import {
  ApiResponse,
  CollaboratorsFormValues,
  CreateTicketRequestData,
  CreateTicketResponse,
  GetTicketFieldResponse,
  GetTicketHistoryResponse,
  GetTicketQueryParams,
  RecaptchaAction,
  TicketCommentSubmissionArgs,
  TicketDetailsAttributes,
  TicketingApiResponse,
  ticketingBaseUrl,
  ticketingRoutes,
} from "@twiliointernal/e2-commons-shared-definitions";
import Client from "./client";
import { getRecaptchaToken } from "@twiliointernal/e2-commons-shared-utils";

class TicketingApi extends Client {
  constructor() {
    super(ticketingBaseUrl);
  }

  getTicketField = async (
    fieldId: string
  ): Promise<TicketingApiResponse<GetTicketFieldResponse>> => {
    const response = await this.get({
      path: ticketingRoutes.getTicketField(fieldId),
    });

    return response as TicketingApiResponse<GetTicketFieldResponse>;
  };

  getTicketById = async (
    ticketId: string
  ): Promise<TicketingApiResponse<TicketDetailsAttributes>> => {
    const csrf = await this.getCsrf();
    const response = await this.get({
      path: ticketingRoutes.getTicketById(ticketId),
      headers: {
        "x-twilio-csrf": csrf,
      },
      requestOptions: {
        credentials: "include",
      },
    });

    return response as TicketingApiResponse<TicketDetailsAttributes>;
  };

  getTicketHistory = async (queryParams: GetTicketQueryParams) => {
    const response: ApiResponse<GetTicketHistoryResponse> = await this.get({
      path: ticketingRoutes.getTicketHistory(queryParams),
      requestOptions: {
        credentials: "include",
      },
    });

    return response as TicketingApiResponse<GetTicketHistoryResponse>;
  };

  createGatedTicket = async (data: CreateTicketRequestData) => {
    const csrf = await this.getCsrf();

    const body: FormData = serialize(data, {
      noFilesWithArrayNotation: true,
      noAttributesWithArrayNotation: true,
    });

    const response: ApiResponse<CreateTicketResponse> = await this.post({
      path: ticketingRoutes.createGatedTicket,
      body,
      headers: {
        "x-twilio-csrf": csrf,
      },
      requestOptions: {
        credentials: "include",
      },
    });

    return response as TicketingApiResponse<CreateTicketResponse>;
  };

  createUngatedTicket = async (
    data: CreateTicketRequestData,
    stargateUngatedUserId: string
  ): Promise<TicketingApiResponse<CreateTicketResponse>> => {
    const csrf = await this.getCsrf();
    const recaptchaToken = await getRecaptchaToken(RecaptchaAction.SUBMIT);
    const body = serialize(
      {
        ...data,
        recaptchaToken,
      },
      {
        noFilesWithArrayNotation: true,
        noAttributesWithArrayNotation: true,
      }
    );
    const response: ApiResponse<CreateTicketResponse> = await this.post({
      path: ticketingRoutes.createUngatedTicket,
      body,
      headers: {
        "x-twilio-csrf": csrf,
        "x-twilio-e2-session-id": stargateUngatedUserId,
      },
      requestOptions: {
        credentials: "include",
      },
    });

    return response as TicketingApiResponse<CreateTicketResponse>;
  };

  updateTicket = async (
    ticketId: string,
    data: TicketCommentSubmissionArgs | CollaboratorsFormValues
  ): Promise<TicketingApiResponse<TicketDetailsAttributes>> => {
    const csrf = await this.getCsrf();
    const body = serialize(data, {
      noFilesWithArrayNotation: true,
      noAttributesWithArrayNotation: true,
    });
    const response = await this.put({
      path: ticketingRoutes.updateTicket(ticketId),
      body,
      headers: {
        "x-twilio-csrf": csrf,
      },
      requestOptions: {
        credentials: "include",
      },
    });

    return response as TicketingApiResponse<TicketDetailsAttributes>;
  };
}

export default new TicketingApi();
